// src/components/projects/ChatWindow/hooks/useModelManagement.js

import { useState, useEffect, useCallback } from 'react';
import {
  getAIModels_API,
  saveAICredentials_API,
  testAICredentials_API,
  getAICredentials_API,
  deleteAICredentials_API,
  isSuccess,
  getResponseData,
  getErrorMessage,
} from '../../api/controller/artifactController';

export const useModelManagement = (companyId, userDetail, handleSnackbar) => {
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelCredentials, setModelCredentials] = useState({});
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);
  const [failedModels, setFailedModels] = useState({});
  const [isRefreshingModel, setIsRefreshingModel] = useState(false);

  const defaultModels = [
    {
      id: 'floxpad-ai',
      model: 'FLOXPAD_LLAMA',
      modelCode: 'FLOXPAD_LLAMA',
      label: 'Floxpad AI',
      provider: 'floxpad',
      status: 'OPEN',
      configured: true,
      description: 'Default Floxpad AI model - Always available',
      isDefault: true,
      builtIn: true
    }
  ];

  const fetchAvailableModels = useCallback(async () => {
    try {
      setIsLoadingModels(true);
      const response = await getAIModels_API(companyId);

      if (isSuccess(response)) {
        const data = getResponseData(response);
        let models = data?.models || [];

        models = models.map(model => {
          if (model.model === 'FLOXPAD_LLAMA' ||
            model.model === 'floxpad-ai' ||
            model.id === 'floxpad-ai' ||
            model.provider === 'floxpad' ||
            model.modelCode === 'FLOXPAD_LLAMA') {
            return {
              ...model,
              builtIn: true,
              credentialStatus: 'OPEN',
              status: 'OPEN',
              configured: true,
              model: 'FLOXPAD_LLAMA',
              modelCode: 'FLOXPAD_LLAMA',
              companyId: companyId,
            };
          }
          return {
            ...model,
            companyId: model.companyId || companyId,
          };
        });

        setAvailableModels(models);

        const defaultModel = models.find(m => m.builtIn === true) ||
          models.find(m => m.credentialStatus === 'OPEN' && !failedModels[m.id || m.model]) ||
          models.find(m => m.credentialStatus === 'OPEN') ||
          models[0];
        if (defaultModel) setSelectedModel(defaultModel);
      } else {
        setAvailableModels(defaultModels);
        setSelectedModel(defaultModels[0]);
      }
    } catch (error) {
      setAvailableModels(defaultModels);
      setSelectedModel(defaultModels[0]);
    } finally {
      setIsLoadingModels(false);
    }
  }, [companyId, failedModels]);

  const fetchProviderCredentials = useCallback(async (provider) => {
    try {
      setIsLoadingCredentials(true);

      if (!companyId) {
        console.warn('No company ID available');
        return null;
      }

      const response = await getAICredentials_API(companyId);

      if (isSuccess(response)) {
        const data = getResponseData(response);
        const credentials = data?.credentials || [];

        const providerCred = credentials.find(c =>
          (c.provider === provider ||
            c.provider?.toLowerCase() === provider?.toLowerCase()) &&
          c.companyId === companyId
        );

        setModelCredentials(prev => ({
          ...prev,
          [provider]: providerCred || null
        }));

        setAvailableModels(prev =>
          prev.map(model => {
            if ((model.provider === provider || model.id === provider) &&
              !model.builtIn) {
              const isConfigured = !!providerCred && providerCred.companyId === companyId;
              const isFailed = failedModels[model.id || model.model] || false;
              return {
                ...model,
                configured: isConfigured && !isFailed,
                status: isConfigured && !isFailed ? 'OPEN' : 'LOCKED',
                credentialStatus: isConfigured && !isFailed ? 'OPEN' : 'LOCKED',
                companyId: providerCred?.companyId || null,
                isFailed: isFailed
              };
            }
            return model;
          })
        );

        return providerCred;
      }
    } catch (error) {
      console.error('Error fetching provider credentials:', error);
    } finally {
      setIsLoadingCredentials(false);
    }
  }, [companyId, failedModels]);

  const isModelConfigured = useCallback((model) => {
    if (!model) return false;
    if (model.builtIn === true) return true;

    const isOpen = model.credentialStatus === 'OPEN' || model.status === 'OPEN';
    const isForCurrentCompany = model.companyId === companyId || !model.companyId;
    const isNotFailed = !failedModels[model.id || model.model];

    return isOpen && isForCurrentCompany && isNotFailed;
  }, [companyId, failedModels]);

  const refreshModel = useCallback(() => {
    setIsRefreshingModel(true);
    fetchAvailableModels().finally(() => {
      setTimeout(() => setIsRefreshingModel(false), 500);
    });
  }, [fetchAvailableModels]);

  useEffect(() => {
    if (companyId && userDetail?.email) {
      fetchAvailableModels();
    }
  }, [companyId, userDetail?.email, fetchAvailableModels]);

  return {
    availableModels,
    selectedModel,
    setSelectedModel,
    modelCredentials,
    setModelCredentials,
    isLoadingModels,
    isLoadingCredentials,
    failedModels,
    setFailedModels,
    isRefreshingModel,
    setIsRefreshingModel,
    fetchAvailableModels,
    fetchProviderCredentials,
    isModelConfigured,
    refreshModel,
    defaultModels,
  };
};